import React, { useState, useEffect, useCallback } from 'react';
import {
  Buildings, UserCircle, ArrowClockwise,
  CaretDown, CaretRight, PencilSimple, Power, Key, MagnifyingGlass, X, Copy, Check,
  UsersThree, Plus
} from '@phosphor-icons/react/ssr';
import { api } from './services/apiClient';

const PLAN_LABELS  = { trial: 'Prueba', basic: 'Básico', enterprise: 'Empresarial' };
const PLAN_COLORS  = {
  trial:      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  basic:      'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  enterprise: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
};
const ROLE_LABELS  = { superadmin: 'Superadmin', admin: 'Administrador', user: 'Usuario' };
const ROLE_COLORS = {
  superadmin: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  admin:      'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  user:       'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
};

const Badge = ({ text, color }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${color}`}>
    {text}
  </span>
);

const MetricCard = ({ label, value, sub, color = 'var(--primary)' }) => (
  <div className="rounded-xl p-4 flex flex-col gap-1"
    style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
    <p className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>{label}</p>
    <p className="text-2xl font-bold" style={{ color }}>{value ?? '—'}</p>
    {sub && <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{sub}</p>}
  </div>
);

const SuperAdminModule = ({ useAuth, useNotification, BaseButton, BaseInput, BaseCard }) => {
  const { user: currentUser } = useAuth();
  const { addNotification }   = useNotification();

  // ── Tabs ──────────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('tenants'); // 'tenants' | 'operators'

  const [metrics, setMetrics]       = useState(null);
  const [tenants, setTenants]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [expanded, setExpanded]     = useState(new Set());
  const [filter, setFilter]         = useState({ status: 'all', plan: 'all', search: '' });
  const [editModal, setEditModal]   = useState(null);  // tenant object (editable copy)
  const [resetModal, setResetModal] = useState(null);  // { id, username }
  const [tempPassword, setTempPassword] = useState(null);
  const [copied, setCopied]         = useState(false);
  const [saving, setSaving]         = useState(false);

  // ── Operadores ────────────────────────────────────────────────────────────────
  const [operators, setOperators]       = useState([]);
  const [loadingOps, setLoadingOps]     = useState(false);
  const [opModal, setOpModal]           = useState(null); // 'create' | 'edit'
  const [opForm, setOpForm]             = useState({ id: null, username: '', email: '', password: '', role: 'superadmin' });
  const [opResetModal, setOpResetModal] = useState(null);
  const [opTempPassword, setOpTempPassword] = useState(null);
  const [opCopied, setOpCopied]         = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [overview, metricsData] = await Promise.all([
        api.get('/api/superadmin/overview'),
        api.get('/api/superadmin/metrics'),
      ]);
      setTenants(overview ?? []);
      setMetrics(metricsData);
    } catch {
      addNotification('Error al cargar el panel de administración', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadOperators = useCallback(async () => {
    setLoadingOps(true);
    try {
      const allUsers = await api.get('/api/users');
      setOperators(allUsers.filter(u => u.role === 'superadmin'));
    } catch {
      addNotification('Error al cargar operadores', 'error');
    } finally {
      setLoadingOps(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (activeTab === 'operators') loadOperators(); }, [activeTab, loadOperators]);

  // ── Filtrado local ──────────────────────────────────────────────────────────
  const filteredTenants = tenants.filter(t => {
    if (filter.status === 'active'   && !t.isActive) return false;
    if (filter.status === 'inactive' &&  t.isActive) return false;
    if (filter.plan !== 'all' && t.plan !== filter.plan) return false;
    if (filter.search) {
      const q = filter.search.toLowerCase();
      if (!t.name.toLowerCase().includes(q) && !(t.contactEmail ?? '').toLowerCase().includes(q))
        return false;
    }
    return true;
  });

  // ── Expansión de filas ──────────────────────────────────────────────────────
  const toggleExpand = (id) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // ── Acciones tenant ─────────────────────────────────────────────────────────
  const handleToggleTenantActive = async (tenant) => {
    try {
      await api.patch(`/api/tenants/${tenant.id}/active`, { isActive: !tenant.isActive });
      setTenants(prev => prev.map(t => t.id === tenant.id ? { ...t, isActive: !t.isActive } : t));
      addNotification(`Empresa ${tenant.isActive ? 'desactivada' : 'activada'}`, 'success');
    } catch (err) {
      addNotification(err.message || 'Error al cambiar estado', 'error');
    }
  };

  const handleSaveTenant = async () => {
    if (!editModal) return;
    setSaving(true);
    try {
      await api.put(`/api/tenants/${editModal.id}`, { name: editModal.name, plan: editModal.plan });
      setTenants(prev => prev.map(t =>
        t.id === editModal.id ? { ...t, name: editModal.name, plan: editModal.plan } : t
      ));
      addNotification('Empresa actualizada', 'success');
      setEditModal(null);
    } catch (err) {
      addNotification(err.message || 'Error al guardar', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Acciones usuario ─────────────────────────────────────────────────────────
  const handleToggleUserActive = async (tenantId, u) => {
    try {
      await api.patch(`/api/users/${u.id}/active`, { isActive: !u.isActive });
      setTenants(prev => prev.map(t =>
        t.id === tenantId
          ? { ...t, users: t.users.map(usr => usr.id === u.id ? { ...usr, isActive: !u.isActive } : usr) }
          : t
      ));
      addNotification(`Usuario ${u.isActive ? 'desactivado' : 'activado'}`, 'success');
    } catch (err) {
      addNotification(err.message || 'Error al cambiar estado', 'error');
    }
  };

  const handleResetPassword = async () => {
    if (!resetModal) return;
    setSaving(true);
    try {
      const data = await api.patch(`/api/users/${resetModal.id}/reset-password`);
      setTempPassword(data.tempPassword);
      setTenants(prev => prev.map(t => ({
        ...t,
        users: t.users.map(u => u.id === resetModal.id ? { ...u, mustChangePassword: true } : u)
      })));
    } catch (err) {
      addNotification(err.message || 'Error al restablecer contraseña', 'error');
      setResetModal(null);
    } finally {
      setSaving(false);
    }
  };

  const handleCopyPassword = async () => {
    if (!tempPassword) return;
    try {
      await navigator.clipboard.writeText(tempPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      addNotification('No se pudo copiar al portapapeles', 'error');
    }
  };

  const closeResetModal = () => {
    setResetModal(null);
    setTempPassword(null);
    setCopied(false);
  };

  // ── Acciones operadores ─────────────────────────────────────────────────────
  const openCreateOp = () => {
    setOpForm({ id: null, username: '', email: '', password: '', role: 'superadmin' });
    setOpModal('create');
  };

  const openEditOp = (op) => {
    setOpForm({ id: op.id, username: op.username, email: op.email, password: '', role: op.role });
    setOpModal('edit');
  };

  const handleSaveOp = async () => {
    if (!opForm.username.trim() || !opForm.email.trim()) {
      addNotification('Usuario y email son obligatorios', 'error');
      return;
    }
    if (opModal === 'create' && (!opForm.password || opForm.password.length < 6)) {
      addNotification('La contraseña debe tener al menos 6 caracteres', 'error');
      return;
    }
    setSaving(true);
    try {
      if (opModal === 'create') {
        await api.post('/api/users', {
          username: opForm.username.trim().toLowerCase(),
          email: opForm.email.trim().toLowerCase(),
          password: opForm.password,
          role: opForm.role,
          tenantId: 1
        });
        addNotification(`Operador @${opForm.username} creado`, 'success');
      } else {
        await api.put(`/api/users/${opForm.id}`, {
          email: opForm.email.trim().toLowerCase(),
          role: opForm.role
        });
        addNotification(`Operador @${opForm.username} actualizado`, 'success');
      }
      setOpModal(null);
      await loadOperators();
    } catch (err) {
      addNotification(err.message || 'Error al guardar', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleOpActive = async (op) => {
    try {
      await api.patch(`/api/users/${op.id}/active`, { isActive: !op.isActive });
      addNotification(`Operador @${op.username} ${op.isActive ? 'desactivado' : 'activado'}`, 'success');
      await loadOperators();
    } catch (err) {
      addNotification(err.message || 'Error al cambiar estado', 'error');
    }
  };

  const handleResetOpPassword = async () => {
    if (!opResetModal) return;
    setSaving(true);
    try {
      const data = await api.patch(`/api/users/${opResetModal.id}/reset-password`);
      setOpTempPassword(data.tempPassword);
      await loadOperators();
    } catch (err) {
      addNotification(err.message || 'Error al restablecer contraseña', 'error');
      setOpResetModal(null);
    } finally {
      setSaving(false);
    }
  };

  const handleCopyOpPassword = async () => {
    if (!opTempPassword) return;
    try {
      await navigator.clipboard.writeText(opTempPassword);
      setOpCopied(true);
      setTimeout(() => setOpCopied(false), 2000);
    } catch {
      addNotification('No se pudo copiar al portapapeles', 'error');
    }
  };

  const closeOpResetModal = () => {
    setOpResetModal(null);
    setOpTempPassword(null);
    setOpCopied(false);
  };

  // ── Formateo de fecha ────────────────────────────────────────────────────────
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>Cargando panel…</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Título ── */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold" style={{ color: 'var(--text)' }}>Panel Superadmin</h2>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Empresas y operadores de la plataforma</p>
        </div>
        <BaseButton variant="outline" size="sm" icon={ArrowClockwise} onClick={load}>
          Actualizar
        </BaseButton>
      </div>

      {/* ── Tabs ── */}
      <div className="flex gap-0 rounded-lg overflow-hidden border" style={{ borderColor: 'var(--border)', width: 'fit-content' }}>
        {[
          { key: 'tenants', label: 'Empresas', icon: Buildings },
          { key: 'operators', label: 'Operadores de la plataforma', icon: UsersThree },
        ].map(tab => (
          <button key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors"
            style={{
              background: activeTab === tab.key ? 'var(--primary)' : 'var(--surface)',
              color:      activeTab === tab.key ? '#fff' : 'var(--text-secondary)',
            }}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ═══════════════ TAB: EMPRESAS ═══════════════ */}
      {activeTab === 'tenants' && (
        <>
      {/* ── Métricas ── */}
      {metrics && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <MetricCard label="Empresas totales"  value={metrics.totalTenants}           color="var(--primary)" />
          <MetricCard label="Empresas activas"  value={metrics.activeTenants}          color="#22c55e" />
          <MetricCard label="Usuarios totales"  value={metrics.totalUsers}             color="var(--primary)" />
          <MetricCard label="Usuarios activos"  value={metrics.activeUsers}            color="#22c55e" />
          <MetricCard label="Registros este mes" value={metrics.registrationsThisMonth} color="#f59e0b" />
          <MetricCard
            label="Por plan"
            value={`${metrics.trialTenants}/${metrics.basicTenants}/${metrics.enterpriseTenants}`}
            sub="trial / básico / empresarial"
            color="var(--text-secondary)"
          />
        </div>
      )}

      {/* ── Alertas de vencimiento de plan ── */}
      {(() => {
        const expired    = tenants.filter(t => t.isPlanExpired   && t.isActive);
        const expiringSoon = tenants.filter(t => t.isPlanExpiringSoon && t.isActive);
        if (expired.length === 0 && expiringSoon.length === 0) return null;
        return (
          <div className="space-y-2">
            {expired.map(t => (
              <div key={t.id} className="flex items-center gap-3 rounded-lg px-4 py-3 text-sm"
                style={{ background: '#fef2f2', color: '#991b1b', border: '1px solid #fca5a5' }}>
                <span className="font-semibold">Plan vencido:</span>
                <span><strong>{t.name}</strong> — el plan venció el {fmtDate(t.planEndDate)}. El tenant está en modo solo lectura.</span>
              </div>
            ))}
            {expiringSoon.map(t => (
              <div key={t.id} className="flex items-center gap-3 rounded-lg px-4 py-3 text-sm"
                style={{ background: '#fffbeb', color: '#92400e', border: '1px solid #fcd34d' }}>
                <span className="font-semibold">Plan por vencer:</span>
                <span><strong>{t.name}</strong> — quedan {t.planDaysLeft} día{t.planDaysLeft !== 1 ? 's' : ''} (vence el {fmtDate(t.planEndDate)}).</span>
              </div>
            ))}
          </div>
        );
      })()}

      {/* ── Filtros ── */}
      <div className="flex flex-wrap gap-2 items-center">
        {/* Estado */}
        <div className="flex rounded-lg overflow-hidden border" style={{ borderColor: 'var(--border)' }}>
          {['all', 'active', 'inactive'].map(v => (
            <button key={v}
              onClick={() => setFilter(f => ({ ...f, status: v }))}
              className="px-3 py-1.5 text-xs font-medium transition-colors"
              style={{
                background: filter.status === v ? 'var(--primary)' : 'var(--surface)',
                color:      filter.status === v ? '#fff' : 'var(--text-secondary)',
              }}
            >
              {{ all: 'Todos', active: 'Activos', inactive: 'Inactivos' }[v]}
            </button>
          ))}
        </div>

        {/* Plan */}
        <select
          value={filter.plan}
          onChange={e => setFilter(f => ({ ...f, plan: e.target.value }))}
          className="rounded-lg px-3 py-1.5 text-xs border"
          style={{ background: 'var(--surface)', color: 'var(--text)', borderColor: 'var(--border)' }}
        >
          <option value="all">Todos los planes</option>
          <option value="trial">Prueba</option>
          <option value="basic">Básico</option>
          <option value="enterprise">Empresarial</option>
        </select>

        {/* Búsqueda */}
        <div className="flex items-center gap-2 rounded-lg px-3 py-1.5 flex-1 min-w-40 border"
          style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
          <MagnifyingGlass size={14} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
          <input
            type="text"
            placeholder="Buscar por nombre o email…"
            value={filter.search}
            onChange={e => setFilter(f => ({ ...f, search: e.target.value }))}
            className="bg-transparent text-xs outline-none w-full"
            style={{ color: 'var(--text)' }}
          />
          {filter.search && (
            <button onClick={() => setFilter(f => ({ ...f, search: '' }))}>
              <X size={12} style={{ color: 'var(--text-secondary)' }} />
            </button>
          )}
        </div>
      </div>

      {/* ── Tabla ── */}
      <div className="rounded-xl overflow-hidden border" style={{ borderColor: 'var(--border)' }}>
        {/* Cabecera */}
        <div className="grid grid-cols-12 gap-2 px-4 py-2 text-xs font-semibold uppercase tracking-wide"
          style={{ background: 'var(--surface-raised)', color: 'var(--text-secondary)' }}>
          <div className="col-span-1" />
          <div className="col-span-3">Empresa</div>
          <div className="col-span-2">Plan</div>
          <div className="col-span-1 text-center">Usuarios</div>
          <div className="col-span-2">Último acceso</div>
          <div className="col-span-2">Estado</div>
          <div className="col-span-1 text-right">Acciones</div>
        </div>

        {filteredTenants.length === 0 && (
          <div className="py-12 text-center text-sm" style={{ color: 'var(--text-secondary)' }}>
            No hay empresas que coincidan con los filtros.
          </div>
        )}

        {filteredTenants.map(tenant => (
          <div key={tenant.id} style={{ borderTop: '1px solid var(--border)' }}>
            {/* Fila tenant */}
            <div className="grid grid-cols-12 gap-2 px-4 py-3 items-center hover:bg-[var(--surface-raised)] transition-colors">
              {/* Expand */}
              <div className="col-span-1">
                <button
                  onClick={() => toggleExpand(tenant.id)}
                  className="p-1 rounded transition-colors hover:bg-[var(--surface-raised)]"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  {expanded.has(tenant.id)
                    ? <CaretDown size={14} />
                    : <CaretRight size={14} />}
                </button>
              </div>

              {/* Nombre */}
              <div className="col-span-3">
                <div className="flex items-center gap-2">
                  <Buildings size={16} style={{ color: 'var(--primary)', flexShrink: 0 }} />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate" style={{ color: 'var(--text)' }}>{tenant.name}</p>
                    <p className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}>
                      {tenant.isIndividual ? 'Persona natural' : 'Empresa'} · {tenant.slug}
                    </p>
                  </div>
                </div>
              </div>

              {/* Plan */}
              <div className="col-span-2 flex flex-col gap-0.5">
                <Badge text={PLAN_LABELS[tenant.plan] ?? tenant.plan} color={PLAN_COLORS[tenant.plan] ?? ''} />
                {tenant.isPlanExpired && (
                  <span className="text-xs font-medium text-red-600">Vencido</span>
                )}
                {tenant.isPlanExpiringSoon && (
                  <span className="text-xs font-medium text-amber-600">{tenant.planDaysLeft}d restantes</span>
                )}
              </div>

              {/* Usuarios */}
              <div className="col-span-1 text-center text-sm font-medium" style={{ color: 'var(--text)' }}>
                {tenant.userCount}
              </div>

              {/* Último acceso */}
              <div className="col-span-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
                {fmtDate(tenant.lastAccessAt)}
              </div>

              {/* Estado */}
              <div className="col-span-2">
                <Badge
                  text={tenant.isActive ? 'Activa' : 'Inactiva'}
                  color={tenant.isActive
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'}
                />
              </div>

              {/* Acciones */}
              <div className="col-span-1 flex justify-end gap-1">
                <button
                  title="Editar empresa"
                  onClick={() => setEditModal({ ...tenant })}
                  className="p-1.5 rounded-lg transition-colors hover:bg-[var(--surface-raised)]"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  <PencilSimple size={15} />
                </button>
                <button
                  title={tenant.isActive ? 'Desactivar empresa' : 'Activar empresa'}
                  onClick={() => handleToggleTenantActive(tenant)}
                  className="p-1.5 rounded-lg transition-colors hover:bg-[var(--surface-raised)]"
                  style={{ color: tenant.isActive ? '#f59e0b' : '#22c55e' }}
                >
                  <Power size={15} />
                </button>
              </div>
            </div>

            {/* Filas de usuarios (expandible) */}
            {expanded.has(tenant.id) && (
              <div style={{ background: 'var(--surface-raised)', borderTop: '1px solid var(--border)' }}>
                {/* Cabecera usuarios */}
                <div className="grid grid-cols-12 gap-2 px-8 py-1.5 text-xs font-semibold uppercase tracking-wide"
                  style={{ color: 'var(--text-secondary)', borderBottom: '1px solid var(--border)' }}>
                  <div className="col-span-3">Usuario</div>
                  <div className="col-span-3">Email</div>
                  <div className="col-span-2">Rol</div>
                  <div className="col-span-2">Último acceso</div>
                  <div className="col-span-1">Estado</div>
                  <div className="col-span-1 text-right">Acciones</div>
                </div>

                {(tenant.users ?? []).length === 0 && (
                  <p className="px-8 py-3 text-xs" style={{ color: 'var(--text-secondary)' }}>
                    Sin usuarios registrados.
                  </p>
                )}

                {(tenant.users ?? []).map(u => (
                  <div key={u.id} className="grid grid-cols-12 gap-2 px-8 py-2.5 items-center"
                    style={{ borderBottom: '1px solid var(--border)' }}>
                    {/* Usuario */}
                    <div className="col-span-3 flex items-center gap-2">
                      <UserCircle size={16} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
                      <div className="min-w-0">
                        <p className="text-xs font-medium truncate" style={{ color: 'var(--text)' }}>
                          {u.fullName || u.username}
                        </p>
                        {u.fullName && (
                          <p className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}>@{u.username}</p>
                        )}
                        {u.mustChangePassword && (
                          <span className="text-[10px] text-orange-500">contraseña temporal</span>
                        )}
                      </div>
                    </div>

                    {/* Email */}
                    <div className="col-span-3 text-xs truncate" style={{ color: 'var(--text-secondary)' }}>
                      {u.email}
                    </div>

                    {/* Rol */}
                    <div className="col-span-2">
                      <Badge
                        text={ROLE_LABELS[u.role] ?? u.role}
                        color={u.role === 'admin'
                          ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
                          : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'}
                      />
                    </div>

                    {/* Último acceso */}
                    <div className="col-span-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
                      {fmtDate(u.lastLoginAt)}
                    </div>

                    {/* Estado */}
                    <div className="col-span-1">
                      <Badge
                        text={u.isActive ? 'Activo' : 'Inactivo'}
                        color={u.isActive
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'}
                      />
                    </div>

                    {/* Acciones */}
                    <div className="col-span-1 flex justify-end gap-1">
                      <button
                        title="Restablecer contraseña"
                        onClick={() => { setResetModal({ id: u.id, username: u.username }); setTempPassword(null); }}
                        className="p-1.5 rounded-lg transition-colors hover:bg-[var(--surface)]"
                        style={{ color: 'var(--text-secondary)' }}
                      >
                        <Key size={14} />
                      </button>
                      <button
                        title={u.isActive ? 'Desactivar usuario' : 'Activar usuario'}
                        onClick={() => handleToggleUserActive(tenant.id, u)}
                        className="p-1.5 rounded-lg transition-colors hover:bg-[var(--surface)]"
                        style={{ color: u.isActive ? '#f59e0b' : '#22c55e' }}
                      >
                        <Power size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ── Modal: Editar tenant ── */}
      {editModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'var(--overlay)' }}
          onClick={e => { if (e.target === e.currentTarget) setEditModal(null); }}
        >
          <div className="w-full max-w-md rounded-2xl p-6 shadow-2xl"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-semibold" style={{ color: 'var(--text)' }}>Editar empresa</h3>
              <button onClick={() => setEditModal(null)} style={{ color: 'var(--text-secondary)' }}><X size={18} /></button>
            </div>

            <div className="space-y-4">
              <BaseInput
                label="Nombre de la empresa"
                value={editModal.name}
                onChange={e => setEditModal(m => ({ ...m, name: e.target.value }))}
              />
              <div>
                <label htmlFor="edit-tenant-plan" className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>
                  Plan
                </label>
                <select
                  id="edit-tenant-plan"
                  value={editModal.plan}
                  onChange={e => setEditModal(m => ({ ...m, plan: e.target.value }))}
                  className="w-full rounded-lg px-3 py-2 text-sm border"
                  style={{ background: 'var(--surface-raised)', color: 'var(--text)', borderColor: 'var(--border)' }}
                >
                  <option value="trial">Prueba (trial)</option>
                  <option value="basic">Básico (basic)</option>
                  <option value="enterprise">Empresarial (enterprise)</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <BaseButton variant="outline" size="sm" onClick={() => setEditModal(null)}>Cancelar</BaseButton>
              <BaseButton size="sm" onClick={handleSaveTenant} disabled={saving}>
                {saving ? 'Guardando…' : 'Guardar cambios'}
              </BaseButton>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Restablecer contraseña ── */}
      {resetModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'var(--overlay)' }}
          onClick={e => { if (e.target === e.currentTarget) closeResetModal(); }}
        >
          <div className="w-full max-w-sm rounded-2xl p-6 shadow-2xl"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold" style={{ color: 'var(--text)' }}>Restablecer contraseña</h3>
              <button onClick={closeResetModal} style={{ color: 'var(--text-secondary)' }}><X size={18} /></button>
            </div>

            {!tempPassword ? (
              // Confirmación
              <>
                <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
                  Se generará una contraseña temporal para <strong style={{ color: 'var(--text)' }}>@{resetModal.username}</strong>.
                  Deberás compartirla manualmente con el usuario. Al ingresar, se le obligará a cambiarla.
                </p>
                <div className="flex justify-end gap-2">
                  <BaseButton variant="outline" size="sm" onClick={closeResetModal}>Cancelar</BaseButton>
                  <BaseButton size="sm" onClick={handleResetPassword} disabled={saving}>
                    {saving ? 'Generando…' : 'Confirmar'}
                  </BaseButton>
                </div>
              </>
            ) : (
              // Resultado: contraseña temporal
              <>
                <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>
                  Copia esta contraseña y compártela con el usuario. No se mostrará de nuevo.
                </p>
                <div className="flex items-center gap-2 rounded-lg p-3 mb-4"
                  style={{ background: 'var(--surface-raised)', border: '1px solid var(--border)' }}>
                  <code className="flex-1 text-sm font-mono tracking-wider" style={{ color: 'var(--text)' }}>
                    {tempPassword}
                  </code>
                  <button
                    onClick={handleCopyPassword}
                    className="p-1.5 rounded transition-colors"
                    style={{ color: copied ? '#22c55e' : 'var(--text-secondary)' }}
                    title="Copiar al portapapeles"
                  >
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                </div>
                <BaseButton className="w-full" onClick={closeResetModal}>Cerrar</BaseButton>
              </>
            )}
          </div>
        </div>
      )}
        </>
      )}

      {/* ═══════════════ TAB: OPERADORES ═══════════════ */}
      {activeTab === 'operators' && (
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold" style={{ color: 'var(--text)' }}>
                Operadores de la plataforma
              </h3>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                Usuarios con acceso total al sistema
              </p>
            </div>
            <BaseButton variant="primary" size="sm" icon={Plus} onClick={openCreateOp}>
              Nuevo operador
            </BaseButton>
          </div>

          {/* Tabla operadores */}
          {loadingOps ? (
            <div className="flex items-center justify-center h-48">
              <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>Cargando…</div>
            </div>
          ) : (
            <div className="rounded-xl overflow-hidden border" style={{ borderColor: 'var(--border)' }}>
              <div className="grid grid-cols-12 gap-2 px-4 py-2 text-xs font-semibold uppercase tracking-wide"
                style={{ background: 'var(--surface-raised)', color: 'var(--text-secondary)' }}>
                <div className="col-span-3">Usuario</div>
                <div className="col-span-3">Email</div>
                <div className="col-span-2">Rol</div>
                <div className="col-span-2">Último acceso</div>
                <div className="col-span-1">Estado</div>
                <div className="col-span-1 text-right">Acciones</div>
              </div>
              {operators.length === 0 && (
                <div className="py-12 text-center text-sm" style={{ color: 'var(--text-secondary)' }}>
                  No hay operadores registrados.
                </div>
              )}
              {operators.map(op => (
                <div key={op.id} className="grid grid-cols-12 gap-2 px-4 py-3 items-center"
                  style={{ borderTop: '1px solid var(--border)' }}>
                  <div className="col-span-3 flex items-center gap-2">
                    <UserCircle size={16} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate" style={{ color: 'var(--text)' }}>
                        {op.fullName || op.username}
                      </p>
                      {op.fullName && (
                        <p className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}>@{op.username}</p>
                      )}
                    </div>
                  </div>
                  <div className="col-span-3 text-xs truncate" style={{ color: 'var(--text-secondary)' }}>{op.email}</div>
                  <div className="col-span-2">
                    <Badge text={ROLE_LABELS[op.role] ?? op.role} color={ROLE_COLORS[op.role] ?? ROLE_COLORS.user} />
                  </div>
                  <div className="col-span-2 text-xs" style={{ color: 'var(--text-secondary)' }}>{fmtDate(op.lastLoginAt)}</div>
                  <div className="col-span-1">
                    <Badge text={op.isActive ? 'Activo' : 'Inactivo'}
                      color={op.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'} />
                  </div>
                  <div className="col-span-1 flex justify-end gap-1">
                    <button title="Editar" onClick={() => openEditOp(op)}
                      className="p-1.5 rounded-lg transition-colors hover:bg-[var(--surface-raised)]"
                      style={{ color: 'var(--text-secondary)' }}>
                      <PencilSimple size={15} />
                    </button>
                    <button title="Restablecer contraseña"
                      onClick={() => { setOpResetModal({ id: op.id, username: op.username }); setOpTempPassword(null); }}
                      className="p-1.5 rounded-lg transition-colors hover:bg-[var(--surface-raised)]"
                      style={{ color: 'var(--text-secondary)' }}>
                      <Key size={14} />
                    </button>
                    <button title={op.isActive ? 'Desactivar' : 'Activar'}
                      onClick={() => handleToggleOpActive(op)}
                      className="p-1.5 rounded-lg transition-colors hover:bg-[var(--surface-raised)]"
                      style={{ color: op.isActive ? '#f59e0b' : '#22c55e' }}>
                      <Power size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Modal: Crear / Editar operador */}
          {opModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
              style={{ background: 'var(--overlay)' }}
              onClick={e => { if (e.target === e.currentTarget) setOpModal(null); }}
            >
              <div className="w-full max-w-md rounded-2xl p-6 shadow-2xl"
                style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-base font-semibold" style={{ color: 'var(--text)' }}>
                    {opModal === 'create' ? 'Nuevo operador' : 'Editar operador'}
                  </h3>
                  <button onClick={() => setOpModal(null)} style={{ color: 'var(--text-secondary)' }}><X size={18} /></button>
                </div>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="op-modal-username" className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Nombre de usuario</label>
                    <input id="op-modal-username" type="text" value={opForm.username} disabled={opModal === 'edit'}
                      onChange={e => setOpForm(f => ({ ...f, username: e.target.value }))}
                      className="w-full rounded-lg px-3 py-2 text-sm border"
                      style={{ background: 'var(--surface-raised)', color: 'var(--text)', borderColor: 'var(--border)' }}
                      placeholder="ej: operador1" />
                  </div>
                  <div>
                    <label htmlFor="op-modal-email" className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Email</label>
                    <input id="op-modal-email" type="email" value={opForm.email}
                      onChange={e => setOpForm(f => ({ ...f, email: e.target.value }))}
                      className="w-full rounded-lg px-3 py-2 text-sm border"
                      style={{ background: 'var(--surface-raised)', color: 'var(--text)', borderColor: 'var(--border)' }}
                      placeholder="operador@10s.co" />
                  </div>
                  {opModal === 'create' && (
                    <div>
                      <label htmlFor="op-modal-password" className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Contraseña</label>
                      <input id="op-modal-password" type="password" value={opForm.password}
                        onChange={e => setOpForm(f => ({ ...f, password: e.target.value }))}
                        className="w-full rounded-lg px-3 py-2 text-sm border"
                        style={{ background: 'var(--surface-raised)', color: 'var(--text)', borderColor: 'var(--border)' }}
                        placeholder="Mínimo 6 caracteres" />
                    </div>
                  )}
                  {opModal === 'edit' && (
                    <div className="rounded-lg px-4 py-3 text-xs"
                      style={{ background: 'var(--surface)', border: '1px dashed var(--border)', color: 'var(--text-secondary)' }}>
                      💡 Para cambiar la contraseña, usa el botón "Restablecer contraseña" en la tabla.
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Rol</label>
                    {opModal === 'create' ? (
                      <div className="rounded-lg px-4 py-3 text-sm font-medium"
                        style={{ background: 'var(--surface-raised)', color: 'var(--text)' }}>
                        Superadmin
                      </div>
                    ) : (
                      <Badge text={ROLE_LABELS[opForm.role] ?? opForm.role} color={ROLE_COLORS[opForm.role] ?? ROLE_COLORS.user} />
                    )}
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-6">
                  <BaseButton variant="outline" size="sm" onClick={() => setOpModal(null)}>Cancelar</BaseButton>
                  <BaseButton size="sm" onClick={handleSaveOp} disabled={saving}>
                    {saving ? 'Guardando…' : 'Guardar'}
                  </BaseButton>
                </div>
              </div>
            </div>
          )}

          {/* Modal: Restablecer contraseña operador */}
          {opResetModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
              style={{ background: 'var(--overlay)' }}
              onClick={e => { if (e.target === e.currentTarget) closeOpResetModal(); }}
            >
              <div className="w-full max-w-sm rounded-2xl p-6 shadow-2xl"
                style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-semibold" style={{ color: 'var(--text)' }}>Restablecer contraseña</h3>
                  <button onClick={closeOpResetModal} style={{ color: 'var(--text-secondary)' }}><X size={18} /></button>
                </div>
                {!opTempPassword ? (
                  <>
                    <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
                      Se generará una contraseña temporal para <strong style={{ color: 'var(--text)' }}>@{opResetModal.username}</strong>.
                    </p>
                    <div className="flex justify-end gap-2">
                      <BaseButton variant="outline" size="sm" onClick={closeOpResetModal}>Cancelar</BaseButton>
                      <BaseButton size="sm" onClick={handleResetOpPassword} disabled={saving}>
                        {saving ? 'Generando…' : 'Confirmar'}
                      </BaseButton>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>
                      Copia esta contraseña. No se mostrará de nuevo.
                    </p>
                    <div className="flex items-center gap-2 rounded-lg p-3 mb-4"
                      style={{ background: 'var(--surface-raised)', border: '1px solid var(--border)' }}>
                      <code className="flex-1 text-sm font-mono tracking-wider" style={{ color: 'var(--text)' }}>{opTempPassword}</code>
                      <button onClick={handleCopyOpPassword}
                        className="p-1.5 rounded transition-colors"
                        style={{ color: opCopied ? '#22c55e' : 'var(--text-secondary)' }}>
                        {opCopied ? <Check size={16} /> : <Copy size={16} />}
                      </button>
                    </div>
                    <BaseButton className="w-full" onClick={closeOpResetModal}>Cerrar</BaseButton>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SuperAdminModule;
